import type { CustomerEvent } from "@redotech/redo-customer-event-common/customer-event-definition";
import type { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import { BasicIndex, parseLoadSubsetOptions } from "@tanstack/db";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { supportTicketQueryCollectionOptions } from "./support-ticket-query-collection-options";
import {
  supportTicketQueryScopeKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";
import {
  getCustomerIdScope,
  getEmailFilterValue,
  getTeamIdScope,
  planOrdersLoad,
  type OrderWithPickStatus,
  type SupportTicketRelatedRecordsApi,
} from "./support-ticket-related-records-requests";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const SUPPORT_TICKET_RELATED_RECORD_LIMIT = 10;

export const supportTicketOrderQueryKeys = {
  all: ["orders"] as const,
  collection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketOrderQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  byEmail: (email: string, scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketOrderQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
      "byEmail",
      email,
    ] as const,
};

export const supportTicketReturnQueryKeys = {
  all: ["returns"] as const,
  collection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketReturnQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  byEmail: (email: string, scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketReturnQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
      "byEmail",
      email,
    ] as const,
};

export const supportTicketCustomerEventQueryKeys = {
  all: ["customerEvents"] as const,
  collection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketCustomerEventQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
};

export function createOrdersCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketRelatedRecordsApi;
  scope?: SupportTicketQueryScope;
}) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey: supportTicketOrderQueryKeys.collection(scope),
      queryFn: async (ctx): Promise<OrderWithPickStatus[]> => {
        const { filters, limit } = parseLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        const plan = planOrdersLoad(filters);
        if (!plan) {
          return [];
        }
        return api.getOrders(plan.teamId, {
          mongoCustomerId: plan.mongoCustomerId,
          emailFallback: plan.emailFallback,
          limit,
        });
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      recoverTransientFailures: false,
    }),
  );
}

export function createReturnsCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketRelatedRecordsApi;
  scope?: SupportTicketQueryScope;
}) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey: supportTicketReturnQueryKeys.collection(scope),
      queryFn: async (ctx): Promise<MerchantAppReturn[]> => {
        const { filters, limit } = parseLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        const email = getEmailFilterValue(filters, "shipping_address", false);
        const teamId = getTeamIdScope(filters);
        if (!email || !teamId) {
          return [];
        }
        return api.getReturnsByEmail(teamId, email, { limit });
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      recoverTransientFailures: false,
    }),
  );
}

export function createCustomerEventsCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketRelatedRecordsApi;
  scope?: SupportTicketQueryScope;
}) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey: supportTicketCustomerEventQueryKeys.collection(scope),
      queryFn: async (ctx): Promise<CustomerEvent[]> => {
        const { filters, limit } = parseLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        const customerId = getCustomerIdScope(filters);
        const teamId = getTeamIdScope(filters);
        if (!customerId || !teamId) {
          return [];
        }
        return api.getCustomerEventsByCustomerId(teamId, customerId, { limit });
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      recoverTransientFailures: false,
    }),
  );
}

export type OrdersCollection = ReturnType<typeof createOrdersCollection>;
export type ReturnsCollection = ReturnType<typeof createReturnsCollection>;
export type CustomerEventsCollection = ReturnType<
  typeof createCustomerEventsCollection
>;

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type { CustomerEvent } from "@redotech/redo-customer-event-common/customer-event-definition";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  getEmailFilterValue,
  type OrderWithPickStatus,
  type SupportTicketRelatedRecordsApi,
} from "./support-ticket-related-records-requests";
